import config from 'Cloud/config';
import { extInfo } from 'lib/extInfo';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { getFeatureShareMountEditAccessDisable, getFeatureWopiCoEdit } from 'reactApp/modules/features/features.selectors';
import { isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';
import { EEditors } from 'server/helpers/editors/getEditorsConfig';

import { CreatorsDescRecord, Editor, EditorState, Extension } from './editor.types';

export const getEditorState = (state: RootState): EditorState => state.editor;

const CO_EDITORS = [EEditors.MYOFFICE, EEditors.R7];

export const getEditorItemById = createSelector(
    getEditorState,
    (_: RootState, id: string) => id,
    (state, id) => {
        if (state.item && state.item.id === id) {
            return state.item;
        }
    }
);

const getDefaultCoEditorId = createSelector(
    (state: RootState) => state,
    (_, item: any) => item,
    (state: RootState, item: any) => isMountedOrSharedFolder(state, item),
    getEditorState,
    (_, item, folderState, editor): string | undefined | null => {
        if (!item) {
            return null;
        }
        const { sharedExtensions, extensions, availableEditors } = editor;
        const hasSharedEditor = editor.hasSharedEditor && sharedExtensions[item.ext];
        const isSharedFolder = hasSharedEditor && folderState.is;

        const editors = extensions[(IS_ONPREMISE && item.ext ? item.ext.toLowerCase() : item.ext) as Extension]?.editors || [];

        return editors.find((editorId) => {
            if (!CO_EDITORS.includes(editorId as EEditors)) {
                return false;
            }
            return hasSharedEditor && isSharedFolder
                ? availableEditors[editorId].default !== 'shared-folder'
                : availableEditors[editorId].default || editors.length === 1;
        });
    }
);

export const getIsFileForCoEditing = createSelector(
    (state: RootState) => state,
    (_: RootState, item: any) => item,
    (state: RootState, item: any) => getDefaultCoEditorId(state, item),
    (state) => getFeatureWopiCoEdit(state),
    (state) => getFeatureShareMountEditAccessDisable(state),
    (_, item, defaultEditor, wopiCoEdit, isShareMountEditAccessDisable) => {
        if (!item || item.isFolder) {
            return false;
        }

        switch (defaultEditor) {
            case EEditors.R7:
                return true;
            case EEditors.MYOFFICE: {
                if (!wopiCoEdit) {
                    return false;
                }

                // запрещаем шарить на редактирование документ, если мы не владельцы
                return isShareMountEditAccessDisable ? !item.isMounted : true;
            }
        }
    }
);

export const getCreateDescriptions = createSelector(getEditorState, ({ extensions }) =>
    Object.values(extensions).reduce((acc, ext) => (ext.creatorsDesc ? acc.concat(ext.creatorsDesc) : acc), [] as CreatorsDescRecord[])
);
export const getEditorItem = createSelector(getEditorState, (state) => state.item);

const ITEM_WITHOUT_HASH_MAX_SIZE = config.get('ITEM_WITHOUT_HASH_MAX_SIZE');

export const getEditorsFor = createSelector(
    (state: RootState) => state,
    (_: RootState, item: any) => item,
    (state: RootState, item) => isMountedOrSharedFolder(state, item),
    (state) => state.editor,
    (_, item, folderState, editor) => {
        let result: Editor[] = [];
        if (!item) {
            return result;
        }

        const { extensions, availableEditors, sharedExtensions } = editor;

        const size = item.size || 0;

        if (size <= ITEM_WITHOUT_HASH_MAX_SIZE) {
            // для файлов размером меньше 21 байта просмотр недоступен
            return result;
        }
        const extData = extInfo.get(item.ext);
        const maxSize = extData.maxEditableSize;

        const hasSharedEditor = editor.hasSharedEditor && sharedExtensions[item.ext];
        const isSharedFolder = hasSharedEditor || folderState.is;

        if (!maxSize || (typeof maxSize === 'number' && size > maxSize)) {
            return result;
        }

        if (
            item.storage !== EStorageType.home &&
            item.storage !== EStorageType.feed &&
            item.storage !== EStorageType.attaches &&
            item.storage !== EStorageType.viewerAttaches &&
            item.storage !== EStorageType.favorites &&
            item.storage !== EStorageType.search
        ) {
            return result;
        }

        const editors = extensions[(IS_ONPREMISE && item.ext ? item.ext.toLowerCase() : item.ext) as Extension]?.editors || [];

        editors.forEach((id) => {
            let editor = availableEditors[id];
            const isDefault = hasSharedEditor && isSharedFolder ? editor.default === 'shared-folder' : !!editor.default;

            if (!hasSharedEditor || !editor['x-in-shared-folder'] || isSharedFolder) {
                editor = Object.assign(
                    {},
                    editor,
                    {
                        icon: editor.icon ? editor.icon[item.ext] : undefined,
                        icon_big: editor.icon_big[item.ext],
                    },
                    {
                        name: editor.id,
                        text: editor.title,
                    }
                );

                if (isDefault) {
                    // default is always first
                    result.unshift(editor);
                } else {
                    result.push(editor);
                }
            }
        });

        result = result.filter((item) => {
            const maxSize =
                typeof extData.maxEditableSize === 'object'
                    ? extData.maxEditableSize[item.id] || extData.maxEditableSize['default']
                    : extData.maxEditableSize;

            return maxSize && size <= maxSize;
        });

        return result;
    }
);

export const getEditorsByExtension = createSelector(
    getEditorState,
    (_: RootState, ext: Extension) => ext,
    ({ extensions }, ext) => (extensions[ext] || { editors: [] }).editors
);

/**
 * Проверяет доступность переименования файла
 * **Важно:** Пока доступно только для WOPI-версии Моего Офиса
 */
export const isEnableRenamingInsideEditor = createSelector(
    (state: RootState) => ({ state, editor: getEditorState(state) }),
    ({ state, editor: { editorId, item } }) => {
        if (!editorId || !item) {
            return false;
        }

        if (editorId === EEditors.MYOFFICE) {
            return ViewerSelectors.isWopiEnabled(state, item.ext.toLowerCase());
        }

        return false;
    }
);

export const isEditorStorage = (storage): boolean =>
    [EStorageType.editor, EStorageType.office, EStorageType.r7, EStorageType.myoffice, EStorageType.myofficeAttaches].includes(storage);
